import HistoricalScenario from '@/types/historical-scenario';
import { XAxisPlotBandsOptions } from 'highcharts';

/**
 * Generates plotBands with bracket-like borders
 * @param selectedScenarios Array of selected historical scenarios
 * @returns Array of plotBand configurations with bracket styling
 */
export const generateHistoricalScenariosPlotBands = (selectedScenarios: HistoricalScenario[]): XAxisPlotBandsOptions[] => {
  if (!selectedScenarios || selectedScenarios.length === 0) {
    return [];
  }

  // Sort scenarios by start date in descending order, first. This places the latest (most recent) scenario at the top.
  const sortedScenariosByStartDate = selectedScenarios.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  const plotBands = sortedScenariosByStartDate.map((scenario): XAxisPlotBandsOptions => {
    const plotBand = {
      from: new Date(scenario.startDate).getTime(),
      to: new Date(scenario.endDate).getTime(),
      color: 'rgba(116, 106, 190, 0.05)',
      zIndex: 0,
    };
    return plotBand;
  });

  return plotBands;
};

/**
 * Generates plotLines for scenario edge markers (vertical lines only)
 * @param selectedScenarios Array of selected historical scenarios
 * @returns Array of plotLine configurations for start/end markers
 */
export const generateScenarioEdgeMarkers = (selectedScenarios: HistoricalScenario[]) => {
  if (!selectedScenarios || selectedScenarios.length === 0) {
    return [];
  }

  const plotLines: any[] = [];

  selectedScenarios.forEach((scenario, index) => {
    plotLines.push({
      value: new Date(scenario.startDate).getTime(),
      color: '#746ABE',
      width: 1,
      zIndex: 1,
      dashStyle: 'Solid',
      id: `scenario-${index}-start`,
    });

    plotLines.push({
      value: new Date(scenario.endDate).getTime(),
      color: '#746ABE',
      width: 1,
      zIndex: 1,
      dashStyle: 'Solid',
      id: `scenario-${index}-end`,
    });
  });

  return plotLines;
};

/**
 * Renders bracket-style borders for historical scenarios using Highcharts custom renderer
 * @param chart The Highcharts chart instance
 * @param selectedScenarios Array of selected historical scenarios
 */
export const renderPlotBandBrackets = (chart: any, selectedScenarios: HistoricalScenario[]) => {
  const xAxis = chart.xAxis[0];
  const stroke = '#746ABE';
  const strokeWidth = 1;
  const bracketLength = 8; // Length of horizontal bracket segments
  const bracketHeight = Math.round(chart.plotHeight);
  const yTop = Math.round(chart.plotTop);

  if (chart.customBrackets) {
    chart.customBrackets.forEach((el: any) => el.destroy());
  }
  chart.customBrackets = [];

  const bracketLineStyle = {
    'stroke-width': strokeWidth,
    stroke,
    zIndex: 0,
    fill: 'none',
    'stroke-linecap': 'butt',
    'shape-rendering': 'crispEdges',
  };

  selectedScenarios.forEach(scenario => {
    let x1 = Math.round(xAxis.toPixels(new Date(scenario.startDate).getTime()));
    let x2 = Math.round(xAxis.toPixels(new Date(scenario.endDate).getTime()));

    if (x1 <= chart.plotLeft) {
      x1 = -(strokeWidth + bracketLength);
    }

    if (x2 <= chart.plotLeft) {
      x2 = -strokeWidth;
    }

    // Left bracket ┌─
    chart.customBrackets.push(
      chart.renderer
        .path([
          ['M', x1 + bracketLength, yTop],
          ['h', -bracketLength],
          ['v', bracketHeight],
          ['h', bracketLength],
        ])
        .attr(bracketLineStyle)
        .add(),
    );

    // Right bracket ─┐
    chart.customBrackets.push(
      chart.renderer
        .path([
          ['M', x2 - bracketLength, yTop],
          ['h', bracketLength],
          ['v', bracketHeight],
          ['h', -bracketLength],
        ])
        .attr(bracketLineStyle)
        .add(),
    );
  });
};

/**
 * Calculates the optimal date range to display all selected historical scenarios
 * @param selectedScenarios Array of selected historical scenarios
 * @returns Object with startDate and endDate that encompasses all scenarios
 */
export const calculateScenarioDateRange = (selectedScenarios: HistoricalScenario[]): { startDate: Date | null; endDate: Date | null } => {
  if (!selectedScenarios || selectedScenarios.length === 0) {
    return { startDate: null, endDate: null };
  }

  const dates = selectedScenarios.map(scenario => ({
    start: new Date(scenario.startDate),
    end: new Date(scenario.endDate),
  }));

  const startDates = dates.map(d => d.start);
  const endDates = dates.map(d => d.end);

  const earliestStart = new Date(Math.min(...startDates.map(d => d.getTime())));
  const latestEnd = new Date(Math.max(...endDates.map(d => d.getTime())));

  // Add some padding before and after to provide context
  const paddedStart = new Date(earliestStart.getTime());
  paddedStart.setMonth(paddedStart.getMonth() - 3);

  const paddedEnd = new Date(latestEnd.getTime());
  paddedEnd.setMonth(paddedEnd.getMonth() + 3);

  return { startDate: paddedStart, endDate: paddedEnd };
};
